import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { EstudioDeTarjetas } from './EstudioDeTarjetas';
import { RegistrarHito, TablaDeHitos } from './Hitos';
import { NuevoIngreso } from './NuevoIngreso';
import { CabeceraDeDia, TarjetaDeCelebracion, agrupaPorDia } from './piezas';
import { useFelicitar, useMes, useProximas, type Celebracion } from './api';

/**
 * Celebraciones.tsx — la pantalla de `#celebraciones`.
 *
 * ## Aquí hay TRES audiencias, no dos
 *
 * Y el vanilla las trataba con una sola pregunta, `Auth.isStaff()`:
 *
 *   · cualquiera con el módulo → ver y felicitar
 *   · `celebraciones:manage`   → registrar hitos, el estudio, la cuarta pestaña
 *   · staff **o líder de área** → el estudio de Nuevo Ingreso
 *
 * Fallaba en las dos direcciones. **De más**: un rol marcado staff sin
 * `celebraciones:manage` veía los tres botones, llenaba el formulario y se lo
 * rechazaban con un 403 al enviarlo — el mismo defecto que Reconocimientos ya
 * tenía corregido. **De menos**: un líder de área que no fuera staff tiene el
 * permiso en el servidor (`newhire_manager`, `celebrations.py:422`) y no veía
 * el botón.
 *
 * El primero se arregla aquí: `Auth.can` lee lo que `/auth/my-modules` ya manda,
 * y ese endpoint existe exactamente para esto.
 *
 * El segundo **no se puede arreglar desde el frontend**: `/auth/me` no dice si
 * alguien lidera un área. Queda en la ficha del módulo. Mientras tanto se
 * conserva `isStaff()` para esa puerta, que es el criterio de antes: así nadie
 * pierde acceso respecto de lo que tenía.
 */

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
               'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

type Pestana = 'proximas' | 'cumpleanos' | 'aniversarios' | 'hitos';

export function Celebraciones() {
  // Lo que de verdad exige el servidor para registrar, generar tarjetas y ver
  // los hitos. Antes era `Auth.isStaff()`, que es otra cosa.
  const puedeGestionar = Auth.can('celebraciones', 'manage');
  // Nuevo Ingreso acepta además a los líderes de área, y eso el frontend no lo
  // sabe. Ver la cabecera del archivo.
  const puedeNuevoIngreso = Auth.isStaff();

  const [pestana, setPestana] = useState<Pestana>('proximas');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [panel, setPanel] = useState<'hito' | 'tarjetas' | 'ingreso' | null>(null);

  const proximas = useProximas();
  const delMes = useMes(mes);
  const felicitar = useFelicitar();

  function felicitarA(userId: number) {
    felicitar.mutate(userId, {
      onSuccess: () => Utils.toast('¡Felicitación enviada!', 'success'),
      onError: (e: unknown) =>
        Utils.toast((e as Error)?.message || 'No se pudo enviar', 'error'),
    });
  }

  const PESTANAS: Array<[Pestana, string, string]> = [
    ['proximas', 'bi-clock', 'Próximas'],
    ['cumpleanos', 'bi-cake2', 'Cumpleaños'],
    ['aniversarios', 'bi-award', 'Aniversarios'],
    ...(puedeGestionar ? ([['hitos', 'bi-flag', 'Hitos registrados']] as Array<[Pestana, string, string]>) : []),
  ];

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">Celebraciones</h1>
          <p className="page-subtitle">Cumpleaños y aniversarios laborales</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {puedeNuevoIngreso && (
            <Button variant="secondary" size="sm" onClick={() => setPanel('ingreso')}>
              <i className="bi bi-person-plus" aria-hidden="true" /> Nuevo Ingreso
            </Button>
          )}
          {puedeGestionar && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setPanel('hito')}>
                <i className="bi bi-heart" aria-hidden="true" /> Registrar celebración
              </Button>
              <Button size="sm" onClick={() => setPanel('tarjetas')}>
                <i className="bi bi-card-image" aria-hidden="true" /> Tarjetas
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="tabs" role="tablist">
        {PESTANAS.map(([t, icono, etiqueta]) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={pestana === t}
            aria-controls={`panel-${t}`}
            id={`tab-${t}`}
            className={`tab-btn${pestana === t ? ' active' : ''}`}
            onClick={() => setPestana(t)}
          >
            <i className={`bi ${icono}`} aria-hidden="true" /> {etiqueta}
          </button>
        ))}
      </div>

      {/* El navegador de mes solo sale donde sirve. En el vanilla vivía en la
          cabecera y se veía también en «Próximas» y en «Hitos registrados», que
          no miran ningún mes: dos pestañas con un control que no hacía nada. */}
      {(pestana === 'cumpleanos' || pestana === 'aniversarios') && (
        <div className="mb-4 flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Mes anterior"
            onClick={() => setMes((m) => (m === 1 ? 12 : m - 1))}
          >
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </Button>
          {/* `aria-live` para que al cambiar de mes se anuncie: sin esto, quien
              usa lector de pantalla pulsa la flecha y no se entera de a dónde
              fue. */}
          <span className="min-w-32 text-center font-bold text-brand-ink" aria-live="polite">
            {MESES[mes - 1]}
          </span>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Mes siguiente"
            onClick={() => setMes((m) => (m === 12 ? 1 : m + 1))}
          >
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </Button>
        </div>
      )}

      <div id={`panel-${pestana}`} role="tabpanel" aria-labelledby={`tab-${pestana}`}>
        {pestana === 'proximas' && (
          <Proximas
            consulta={proximas}
            alFelicitar={felicitarA}
            felicitando={felicitar.isPending}
          />
        )}

        {pestana === 'cumpleanos' && (
          <ListaDelMes
            consulta={delMes}
            items={delMes.data?.birthdays}
            que="cumpleaños"
            icono="bi bi-cake2"
            vacio="Sin cumpleaños este mes"
            detalle={(u) => u.birth_date_display && (
              <div className="celebration-type">
                <i className="bi bi-cake2" aria-hidden="true" /> Cumpleaños {u.birth_date_display}
              </div>
            )}
            alFelicitar={felicitarA}
            felicitando={felicitar.isPending}
          />
        )}

        {pestana === 'aniversarios' && (
          <ListaDelMes
            consulta={delMes}
            items={delMes.data?.anniversaries}
            que="aniversarios laborales"
            icono="bi bi-award"
            vacio="Sin aniversarios laborales este mes"
            detalle={(u) => (
              <div className="celebration-type">
                <i className="bi bi-award" aria-hidden="true" /> {u.years}{' '}
                {u.years === 1 ? 'año' : 'años'} en la empresa
              </div>
            )}
            alFelicitar={felicitarA}
            felicitando={felicitar.isPending}
          />
        )}

        {pestana === 'hitos' && (
          <TablaDeHitos activa={pestana === 'hitos'} alRegistrar={() => setPanel('hito')} />
        )}
      </div>

      <RegistrarHito abierto={panel === 'hito'} alCerrar={() => setPanel(null)} />
      <EstudioDeTarjetas
        abierto={panel === 'tarjetas'}
        alCerrar={() => setPanel(null)}
        alPedirNuevoIngreso={() => setPanel('ingreso')}
      />
      <NuevoIngreso abierto={panel === 'ingreso'} alCerrar={() => setPanel(null)} />
    </>
  );
}

/* ── Próximas ──────────────────────────────────────────────────────────── */

type Consulta = { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };

function Proximas({
  consulta, alFelicitar, felicitando,
}: {
  consulta: Consulta & { data?: { items?: Celebracion[] } };
  alFelicitar: (id: number) => void;
  felicitando: boolean;
}) {
  if (consulta.isLoading) return <Esqueleto />;
  if (consulta.isError) {
    return (
      <EstadoError
        err={consulta.error}
        que="las celebraciones próximas"
        onReintentar={() => { consulta.refetch(); }}
      />
    );
  }
  const items = consulta.data?.items ?? [];
  if (!items.length) {
    return (
      <EstadoVacio
        icono="bi bi-calendar-x"
        titulo="Sin celebraciones en los próximos 60 días"
        descripcion="Vuelve más adelante, o mira el mes que quieras en las otras pestañas."
      />
    );
  }
  return (
    <div className="cel-dias">
      {agrupaPorDia(items).map(([dias, gente]) => (
        <section key={dias} className="cel-dia">
          <CabeceraDeDia dias={dias} cuantos={gente.length} />
          {gente.map((u) => (
            <TarjetaDeCelebracion
              key={`${u.user_id}-${u.type}`}
              c={u}
              alFelicitar={alFelicitar}
              felicitando={felicitando}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

/* ── Cumpleaños / Aniversarios del mes ─────────────────────────────────── */

/** Una sola lista para las dos pestañas.
 *
 *  `renderBirthdays` y `renderAnniversaries` eran la misma función con dos
 *  textos distintos — y **una escapaba `avatar_url` y la otra no**. Dos copias
 *  de lo mismo son dos sitios donde arreglar cada defecto, y uno se queda sin
 *  arreglar. */
function ListaDelMes({
  consulta, items, que, icono, vacio, detalle, alFelicitar, felicitando,
}: {
  consulta: Consulta;
  items?: Celebracion[];
  que: string;
  icono: string;
  vacio: string;
  detalle: (u: Celebracion) => React.ReactNode;
  alFelicitar: (id: number) => void;
  felicitando: boolean;
}) {
  if (consulta.isLoading) return <Esqueleto />;
  if (consulta.isError) {
    return (
      <EstadoError err={consulta.error} que={que} onReintentar={() => { consulta.refetch(); }} />
    );
  }
  const lista = [...(items ?? [])].sort((a, b) => a.days_until - b.days_until);
  if (!lista.length) return <EstadoVacio icono={icono} titulo={vacio} />;

  return (
    <div className="flex flex-col gap-4">
      {lista.map((u) => (
        <TarjetaDeCelebracion
          key={u.user_id}
          c={u}
          detalle={detalle(u)}
          alFelicitar={alFelicitar}
          felicitando={felicitando}
        />
      ))}
    </div>
  );
}

function Esqueleto() {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24" />)}
    </div>
  );
}
